import React, { useCallback, useEffect, useRef, useState } from 'react';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import EmailLeadCapture from '@components/EmailLeadCapture';
import useEventTracking from '@hooks/useEventTracking';
import useScrollPosition from '@hooks/useScrollPosition';
import useWindowSize from '@hooks/useWindowSize';
import { Theme } from '@interfaces/global';
import { share_article_path, subscribe_path } from '@routes';
import { TrackingLocation } from '@tracking';
import { WIDTH_SM } from '@utils/constants';

import { BottomBannerProps } from '.';

type BottomBannerContentProps = Omit<BottomBannerProps, 'env' | 'isGdprRequired' | 'isWebview'>;

const SCROLL_THRESHOLD = 680;

const BottomBannerContent = ({
  articleId,
  buttonText,
  formLocation,
  headline,
  hideUntilScroll,
  isLoggedIn,
  isPro,
  leadDetails,
  leadType,
  submitLink,
}: BottomBannerContentProps) => {
  const BannerElement = useRef(null);
  const footerElement = useRef<HTMLElement>(null);
  const [showBanner, setShowBanner] = useState<boolean>(!hideUntilScroll);
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const { Track } = useEventTracking({ location: TrackingLocation.BottomBanner });
  const Button = isPro ? ProButton : PrimaryButton;

  const showBannerBasedOnScroll = useCallback(
    (currPosY: number) => {
      const absoluteYPos = Math.abs(currPosY);
      const position = windowHeight + absoluteYPos;
      const shouldShowBanner =
        position < footerElement.current?.offsetTop &&
        ((hideUntilScroll && absoluteYPos > SCROLL_THRESHOLD) || !hideUntilScroll);

      setShowBanner(shouldShowBanner);
    },
    [hideUntilScroll, windowHeight]
  );

  useScrollPosition({
    effect: ({ currPos }: { currPos: { x: number; y: number } }) =>
      showBannerBasedOnScroll(currPos.y),
    deps: [footerElement.current],
    wait: 100,
  });

  useEffect(() => {
    footerElement.current = document.getElementById('ti-footer');
    if (window.scrollY) showBannerBasedOnScroll(window.scrollY);
  }, [showBannerBasedOnScroll]);

  return (
    <Track>
      {showBanner && (
        <div
          className="min-h-56 w-full bg-grayscale-charcoal px-16 py-8"
          data-testid="lock-lead-capture-form"
        >
          <div
            className="m-auto flex flex-col items-center justify-center md:flex-row md:gap-16 lg:gap-48"
            ref={BannerElement}
          >
            <div className="my-8 max-w-full text-center font-suisse-neue text-body-md font-normal text-white md:my-0">
              {headline}
            </div>
            <div className="flex w-auto items-center">
              {isLoggedIn ? (
                <Button
                  as="link"
                  size="small"
                  href={submitLink}
                  className="w-auto cursor-pointer touch-manipulation sm-d:w-auto"
                >
                  {buttonText}
                </Button>
              ) : (
                <EmailLeadCapture
                  articleId={articleId}
                  buttonText={buttonText}
                  formClassName="flex items-start sm:items-center flex-col sm:flex-row"
                  formLocation={formLocation}
                  gdprClassName="mb-8 sm:mb-0 sm:mr-32"
                  inputClassName="w-full sm:w-[240px]"
                  isVertical={windowWidth < WIDTH_SM}
                  leadDetails={leadDetails}
                  leadType={leadType}
                  redirectUri={subscribe_path()}
                  theme={Theme.Dark}
                  unlockContent={submitLink === share_article_path()}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </Track>
  );
};

export default BottomBannerContent;
